import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './CSVUpload.css';

function CSVUpload({ type, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  function parseCSV(text) {
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim());
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }
    
    return data;
  }

  async function handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setMessage('Please upload a CSV file');
      return;
    }

    setUploading(true);
    setMessage('Processing...');

    try {
      const text = await file.text();
      const data = parseCSV(text);

      let imported = 0;
      
      if (type === 'chapters') {
        for (const row of data) {
          await addDoc(collection(db, 'chapters'), {
            name: row.CHAPTER || row.name || '',
            state: row.STATE || row.state || '',
            status: row.Status || row.status || 'Active',
            tnfEmailId: row['TNF EMAIL ID'] || row.tnfEmailId || '',
            contactEmail: row['TNF EMAIL ID'] || row.contactEmail || '',
            chairPerson: {
              name: row['Chair Person'] || '',
              phone: row['Chair Phone'] || ''
            },
            secretary: {
              name: row['Secretary'] || '',
              phone: row['Secretary Phone'] || ''
            },
            treasurer: {
              name: row['Treasurer'] || '',
              phone: row['Treasurer Phone'] || ''
            },
            officersStartDate: row['Officers Start Date'] || '',
            sisterDistricts: row['Sister District(s)'] || '',
            claimedProjects: [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          imported++;
        }
      } else if (type === 'projects') {
        for (const row of data) {
          await addDoc(collection(db, 'projects'), {
            title: row.title || row.Title || '',
            description: row.description || row.Description || '',
            category: row.category || row.Category || '',
            location: row.location || row.Location || '',
            budget: parseInt(row.budget || row.Budget || 0),
            requirements: (row.requirements || row.Requirements || '').split(';').filter(r => r.trim()),
            status: row.status || row.Status || 'available',
            claimedBy: null,
            claimedDate: null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          imported++;
        }
      }

      setMessage(`✅ Successfully imported ${imported} ${type}!`);
      if (onUploadComplete) onUploadComplete();
      
      // Clear file input
      event.target.value = '';
      
    } catch (error) {
      console.error('Upload error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }

    setUploading(false);
  }

  function downloadTemplate() {
    let csvContent = '';
    
    if (type === 'chapters') {
      csvContent = 'CHAPTER,STATE,Status,TNF EMAIL ID,Chair Person,Chair Phone,Secretary,Secretary Phone,Treasurer,Treasurer Phone,Officers Start Date,Sister District(s)\n';
      csvContent += 'Austin,TX,Active,austin@tnfusa.org,John Doe,(512) 555-1234,Jane Smith,(512) 555-5678,Bob Wilson,(512) 555-9012,2024-01-01,District 5\n';
    } else if (type === 'projects') {
      csvContent = 'title,description,category,location,budget,requirements,status\n';
      csvContent += 'Sample Project,This is a sample project description,Infrastructure & Facility Upgrades,Tamil Nadu,5000,Requirement 1;Requirement 2;Requirement 3,available\n';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
  }

  return (
    <div className="csv-upload-container">
      <div className="csv-upload-header">
        <h3>Import {type === 'chapters' ? 'Chapters' : 'Projects'} from CSV</h3>
        <button onClick={downloadTemplate} className="template-button">
          📥 Download Template
        </button>
      </div>

      <div className="upload-section">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          disabled={uploading}
          id={`csv-upload-${type}`}
          className="file-input"
        />
        <label htmlFor={`csv-upload-${type}`} className="upload-label">
          {uploading ? '⏳ Uploading...' : '📄 Choose CSV File'}
        </label>
      </div>

      {message && (
        <div className={`upload-message ${message.startsWith('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      <div className="csv-instructions">
        <h4>Instructions:</h4>
        <ol>
          <li>Download the CSV template</li>
          <li>Fill in your data (use Excel or Google Sheets)</li>
          <li>Save as CSV format</li>
          <li>Upload the file using the button above</li>
        </ol>
        {type === 'projects' && (
          <p><strong>Note:</strong> Separate multiple requirements with semicolons (;)</p>
        )}
      </div>
    </div>
  );
}

export default CSVUpload;
